import { getLocaleCurrencySymbol, getLocaleNumberSymbol, NumberSymbol } from '@angular/common';
import { Component, Inject, Input, LOCALE_ID, Optional, Self, SimpleChanges } from '@angular/core';
import { NgControl } from '@angular/forms';
import { FormControlBase } from '../../common/form-control-base/form-control-base';
import { TCM_FORM_CONFIG, TCMFormConfig } from '../../tcm-form-config';
import { MathHelper } from '../../helpers/math-helper';
import { MaskitoOptions } from '@maskito/core';
import { maskitoNumberOptionsGenerator } from '@maskito/kit';

@Component({
    selector: 'waf-currency-input',
    templateUrl: './currency-input.component.html',
    styleUrls: ['./currency-input.component.scss'],
    inputs: ['label', 'disabled', 'hideValidationMessages', 'isReadonlyValue', 'placeholder', 'tabindex'],
    standalone: false
})
export class CurrencyInputComponent extends FormControlBase<number> {

    @Input() sign: string;

    @Input() allowNegative: boolean;

    @Input() max: number;

    internalValue: string;

    maskitoOptions: MaskitoOptions;

    private decimalSymbol: string;
    private thousandSeparator: string;

    constructor(
        @Self() @Optional() protected override ngModel: NgControl,
        @Inject(LOCALE_ID) private locale: string,
        @Optional() @Inject(TCM_FORM_CONFIG) protected config: TCMFormConfig,
    ) {
        super('waf-currency-input', ngModel);
      
        if (config && config.wafCurrencyInput) {
          this.sign = this.config.wafCurrencyInput.currencySign;

          this.allowNegative = this.config.wafCurrencyInput.allowNegative;
        }

        if (!this.sign) {
            this.sign = getLocaleCurrencySymbol(this.locale);
        }
    }

    override onInit() {
        this.setupMask();
    }

    override ngOnChanges(changes: SimpleChanges) {
        if (changes.allowNegative) {
            this.setupMask();
        }
    }

  override onValueWritten() {
    this.internalValue = (this.value != null && this.value.toString() !== '')
      ? MathHelper.roundToTwoDecimalPlaces(this.value).toString().replace('.', this.decimalSymbol)
      : null;
    }
  onInternalValueChanged(value: string) {
    this.internalValue = value;
    if (value && value.trim() !== '') {
      const trimmedValue = String(value).trim();
      const isNegative = trimmedValue.startsWith('-') || trimmedValue.includes('−') || parseFloat(trimmedValue) < 0;
      let cleanValue = trimmedValue.replace(new RegExp('\\' + this.thousandSeparator, 'g'), '').
        replace(this.decimalSymbol, '.').replace('−', '-')
        .replace(/[^\d.-]/g, '');

      if (isNegative && !cleanValue.startsWith('-')) {
        cleanValue = '-' + cleanValue.replace(/^-+/, '');
      }
      if (!/^-?\d*\.?\d*$/.test(cleanValue)) {
        this.value = null;
        return;
      }
      const numericValue = parseFloat(cleanValue);
      this.value = isNaN(numericValue) ? null : numericValue;
    }
    else {
      this.value = null;
    }
  }

    private setupMask() {
        const groupSymbol = getLocaleNumberSymbol(this.locale, NumberSymbol.CurrencyGroup);
        this.decimalSymbol = getLocaleNumberSymbol(this.locale, NumberSymbol.CurrencyDecimal);
        this.thousandSeparator = groupSymbol || ',';

        this.maskitoOptions = maskitoNumberOptionsGenerator({
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
            thousandSeparator: this.thousandSeparator,
            decimalSeparator: this.decimalSymbol,
            min: this.allowNegative ? -999999999.99 : 0,
            max: 999999999.99
        });
    }

    get maxAllowedValue(): number {
        return this.allowNegative ? 999999999.99 : 999999999.99;
    }

    get minAllowedValue(): number {
        return this.allowNegative ? -999999999.99 : 0;
  }


}
